import type { DraftReviewFinding, DraftReviewMetrics } from "@/types/drafting";

export function isOpenFinding(finding: DraftReviewFinding): boolean {
  return finding.status === "open";
}

export function findingStatusLabel(status: string): string {
  switch (status) {
    case "open":
      return "Open";
    case "accepted":
      return "Accepted";
    case "dismissed":
      return "Dismissed";
    case "resolved-manually":
      return "Resolved";
    case "revision-applied":
      return "Revision applied";
    case "intentional":
      return "Intentional";
    default:
      return status.replace(/-/g, " ");
  }
}

export function findingStatusBadgeClass(status: string): string {
  if (status === "open") {
    return "bg-[var(--surface-sunken)] text-[var(--lm-muted)] dark:bg-slate-800 dark:text-slate-300";
  }
  if (status === "accepted" || status === "revision-applied" || status === "resolved-manually") {
    return "bg-green-500/15 text-green-700 dark:bg-green-500/20 dark:text-green-200";
  }
  if (status === "dismissed" || status === "intentional") {
    return "bg-slate-500/15 text-slate-700 dark:bg-slate-500/20 dark:text-slate-300";
  }
  return "bg-[var(--surface-sunken)] text-[var(--lm-muted)] dark:bg-slate-800";
}

export function computeReviewMetrics(
  findings: DraftReviewFinding[],
  playbookPercent?: number
): DraftReviewMetrics {
  const activeIssues = findings.filter(isOpenFinding).length;
  const resolvedItems = findings.length - activeIssues;

  if (typeof playbookPercent === "number") {
    return {
      playbook_conformance_percent: playbookPercent,
      active_issues: activeIssues,
      resolved_items: resolvedItems,
    };
  }

  if (!findings.length) {
    return {
      playbook_conformance_percent: 0,
      active_issues: 0,
      resolved_items: 0,
    };
  }

  const penalty = findings.filter(isOpenFinding).reduce((sum, finding) => {
    if (finding.severity === "high") return sum + 12;
    if (finding.severity === "medium") return sum + 6;
    return sum + 3;
  }, 0);

  return {
    playbook_conformance_percent: Math.max(35, 100 - penalty),
    active_issues: activeIssues,
    resolved_items: resolvedItems,
  };
}

export function severityLabel(severity: string): string {
  switch (severity) {
    case "high":
      return "High priority";
    case "medium":
      return "Medium priority";
    case "low":
      return "Low priority";
    default:
      return severity;
  }
}

export function severityBadgeClass(severity: string): string {
  switch (severity) {
    case "high":
      return "bg-red-500/15 text-red-700 dark:bg-red-500/20 dark:text-red-200";
    case "medium":
      return "bg-amber-500/15 text-amber-800 dark:bg-amber-500/20 dark:text-amber-200";
    case "low":
      return "bg-sky-500/15 text-sky-800 dark:bg-sky-500/20 dark:text-sky-200";
    default:
      return "bg-[var(--surface-sunken)] text-[var(--lm-muted)] dark:bg-slate-800";
  }
}
